import React, { useState, useCallback } from 'react';
import { ShieldCheck, ShieldAlert, AlertTriangle, Loader2 } from 'lucide-react';
import API from '../api/api';

// Shared FMCSA identity check (backend: core/utils/fmcsa.py).
// Used by the Carriers page and the rate confirmation's new-carrier form, so a
// carrier is checked the same way wherever it's entered.

// verified → green; identity problems → red; "couldn't tell" → amber.
const TONE = {
  verified: 'green',
  phone_mismatch: 'red',
  dot_mc_mismatch: 'red',
  not_found: 'red',
  no_phone_on_file: 'amber',
  phone_not_provided: 'amber',
  invalid: 'amber',
  unavailable: 'amber',
};

const TONE_CLASSES = {
  green: { box: 'bg-green-50 border-green-200 text-green-900', badge: 'bg-green-100 text-green-800', Icon: ShieldCheck },
  red:   { box: 'bg-red-50 border-red-200 text-red-900',       badge: 'bg-red-100 text-red-800',     Icon: ShieldAlert },
  amber: { box: 'bg-amber-50 border-amber-200 text-amber-900', badge: 'bg-amber-100 text-amber-800', Icon: AlertTriangle },
  gray:  { box: 'bg-gray-50 border-gray-200 text-gray-700',    badge: 'bg-gray-100 text-gray-600',   Icon: AlertTriangle },
};

const SHORT_LABEL = {
  verified: 'FMCSA verified',
  phone_mismatch: 'Phone mismatch',
  dot_mc_mismatch: 'DOT/MC mismatch',
  not_found: 'Not at FMCSA',
  no_phone_on_file: 'No FMCSA phone',
  phone_not_provided: 'No phone entered',
  invalid: 'Not checked',
  unavailable: 'Check failed',
};

export const isFmcsaVerified = (status) => status === 'verified';

// Compact pill for tables and the rate con's directory picker.
export function FmcsaStatusBadge({ status, checkedAt, className = '' }) {
  const tone = TONE_CLASSES[TONE[status] || 'gray'];
  const { Icon } = tone;
  const title = checkedAt ? `Checked ${new Date(checkedAt).toLocaleString()}` : 'Never checked against FMCSA';
  return (
    <span title={title}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${tone.badge} ${className}`}>
      <Icon size={11} />
      {status ? SHORT_LABEL[status] || status : 'Not checked'}
    </span>
  );
}

// The full result: verdict, what FMCSA has on file, and any authority warnings.
export function FmcsaResultPanel({ result, onUseName, compact = false }) {
  if (!result) return null;
  const tone = TONE_CLASSES[TONE[result.status] || 'gray'];
  const { Icon } = tone;
  const found = result.legal_name || result.usdot_number;

  return (
    <div className={`rounded-md border p-3 text-sm ${tone.box}`}>
      <div className="flex items-start gap-2">
        <Icon size={16} className="mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">{result.message}</p>

          {found && (
            <dl className={`mt-2 grid ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'} gap-x-4 gap-y-0.5 text-xs`}>
              <div className="flex gap-1">
                <dt className="opacity-70 shrink-0">FMCSA name:</dt>
                <dd className="font-medium break-words">
                  {result.legal_name || '—'}
                  {result.dba_name ? <span className="opacity-70"> (DBA {result.dba_name})</span> : null}
                </dd>
              </div>
              <div className="flex gap-1">
                <dt className="opacity-70 shrink-0">USDOT / MC:</dt>
                <dd className="font-medium">{result.usdot_number || '—'} / {result.mc_number || '—'}</dd>
              </div>
              <div className="flex gap-1">
                <dt className="opacity-70 shrink-0">Phone on file:</dt>
                <dd className="font-medium">
                  {result.fmcsa_phones?.length
                    ? result.fmcsa_phones.map((ph, i) => (
                        <span key={ph} className="whitespace-nowrap">{i > 0 && ' / '}{ph}</span>
                      ))
                    : 'none'}
                </dd>
              </div>
              <div className="flex gap-1">
                <dt className="opacity-70 shrink-0">Authority:</dt>
                <dd className="font-medium">
                  {result.authority?.length
                    ? [...new Set(result.authority.map((a) => a.status))].join(', ')
                    : '—'}
                </dd>
              </div>
              {result.address && (
                <div className={`flex gap-1 ${compact ? '' : 'sm:col-span-2'}`}>
                  <dt className="opacity-70 shrink-0">Address:</dt>
                  <dd className="truncate">{result.address}</dd>
                </div>
              )}
            </dl>
          )}

          {result.warnings?.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs font-medium text-red-700">
              {result.warnings.map((w) => <li key={w}>⚠ {w}</li>)}
            </ul>
          )}

          {onUseName && result.legal_name && (
            <button type="button" onClick={() => onUseName(result.legal_name)}
                    className="mt-2 text-xs font-medium underline opacity-80 hover:opacity-100">
              Use FMCSA name
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Run an unsaved check (POST /carriers/fmcsa-check/). `reset` clears a stale
// result when the operator edits the DOT, MC or phone after checking.
export function useFmcsaCheck() {
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const run = useCallback(async ({ usdot_number, mc_number, phone }) => {
    setChecking(true);
    try {
      const res = await API.post('carriers/fmcsa-check/', { usdot_number, mc_number, phone });
      setResult(res.data);
      return res.data;
    } catch {
      const failed = { status: 'unavailable', message: "Couldn't run the FMCSA check. Try again in a minute." };
      setResult(failed);
      return failed;
    } finally {
      setChecking(false);
    }
  }, []);

  const reset = useCallback(() => setResult(null), []);
  return { result, checking, run, reset, setResult };
}

// "Verify with FMCSA" button, consistent across forms.
export function FmcsaCheckButton({ onClick, checking, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={checking || disabled}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 disabled:opacity-50 whitespace-nowrap">
      {checking ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
      {checking ? 'Checking FMCSA…' : 'Verify with FMCSA'}
    </button>
  );
}
